import { Button, Progress, Typography } from "antd";
import { CheckCircleFilled, CloseOutlined, FileOutlined, ReloadOutlined, WarningFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { palette } from "../../theme";
import { fmtBytes } from "./EvidenceDrawer";
import type { QItem } from "./useUploadQueue";

/** Elenco dei file in coda con miniatura, avanzamento, stato e «Riprova». */
export function UploadQueueList(props: { items: QItem[]; busy: boolean; onRemove: (id: string) => void; onRetry?: () => void }) {
  const { items, busy, onRemove, onRetry } = props;
  const { t } = useTranslation();
  if (!items.length) return null;
  return (
    <div style={{ display: "grid", gap: 8, marginTop: 10 }}>
      {items.map((x) => {
        const active = x.status === "preparing" || x.status === "uploading";
        const f = x.prepared ?? x.file;
        return (
          <div key={x.id} style={{ display: "flex", gap: 10, alignItems: "center", padding: 8, border: `1px solid ${x.status === "error" ? palette.bad : palette.hair}`, borderRadius: 8, background: palette.card, minWidth: 0 }}>
            <div style={{ width: 44, height: 44, borderRadius: 6, background: palette.sunk, flex: "none", overflow: "hidden", display: "grid", placeItems: "center" }}>
              {x.preview ? <img src={x.preview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => ((e.currentTarget.style.display = "none"))} /> : <FileOutlined style={{ fontSize: 20, color: palette.muted }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{x.file.name}</div>
              <div style={{ fontSize: 12, color: palette.muted }}>
                {fmtBytes(f.size)}
                {x.prepared && x.prepared.size < x.file.size ? ` (${fmtBytes(x.file.size)})` : ""}
                {" · "}
                {x.status === "ready" ? t("b_up_ready") : null}
                {x.status === "preparing" ? t("b_up_preparing") : null}
                {x.status === "uploading" ? (x.retrying ? `${t("b_up_retrying")} ${x.retrying}/2` : `${t("b_up_uploading")} ${x.progress}%`) : null}
                {x.status === "done" ? <span style={{ color: palette.ok }}>{t("b_up_done")}</span> : null}
                {x.status === "error" ? <span style={{ color: palette.bad }}>{x.error ? t(x.error, { defaultValue: x.error }) : t("b_up_error")}</span> : null}
              </div>
              {active ? <Progress percent={x.status === "preparing" ? 0 : x.progress} showInfo={false} size="small" status={x.retrying ? "exception" : "active"} style={{ margin: 0 }} /> : null}
            </div>
            {x.status === "done" ? <CheckCircleFilled style={{ color: palette.ok, fontSize: 20 }} /> : null}
            {x.status === "error" ? (
              <>
                {onRetry && x.error !== "b_ext_bad" ? <Button size="middle" icon={<ReloadOutlined />} onClick={onRetry} disabled={busy} aria-label={t("b_up_retry")} /> : <WarningFilled style={{ color: palette.bad, fontSize: 20 }} />}
              </>
            ) : null}
            {!busy && x.status !== "done" ? <Button type="text" size="middle" icon={<CloseOutlined />} onClick={() => onRemove(x.id)} aria-label={t("b_up_remove")} /> : null}
          </div>
        );
      })}
    </div>
  );
}

export const UploadHint = () => {
  const { t } = useTranslation();
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      {t("b_allowed_fmt")} · max 20 MB · {t("b_up_hint")}
    </Typography.Text>
  );
};
